// Service 12 — Education document certification (degrees, transcripts, credential recognition).
// Category definitions only (client-bundle safe, no corpus import).

export type EducationFaqCategory =
  | "degree-transcript-certification"
  | "mfa-legalization-education"
  | "certified-translation-education"
  | "apostille-education-2027"
  | "study-abroad-application"
  | "credential-evaluation"
  | "teacher-license-thailand"
  | "school-enrollment-equivalency"
  | "professional-licensing"
  | "embassy-attestation-education";

export interface EducationCategoryDef {
  slug: EducationFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const EDUCATION_FAQ_CATEGORIES: EducationCategoryDef[] = [
  {
    slug: "degree-transcript-certification",
    titleTh: "รับรองปริญญาบัตรและทรานสคริปต์จากสถาบันการศึกษา",
    titleEn: "Certifying Degrees and Transcripts at the Issuing Institution",
    descTh:
      "ขั้นตอนขอสำเนาปริญญาบัตร ใบรับรองการศึกษา (Certificate of Graduation) และทรานสคริปต์ฉบับภาษาอังกฤษจากสำนักทะเบียนของมหาวิทยาลัย การประทับตราสถาบัน และการเตรียมเอกสารก่อนส่งนิติกรณ์กรมการกงสุล",
    descEn:
      "How to obtain certified copies of your degree certificate, certificate of graduation and English-language transcript from the university registrar, what the institutional seal must show, and how to prepare the set before Thai MFA legalisation.",
    matchTh: /(ปริญญาบัตร|ทรานสคริปต์|ใบรับรองการศึกษา|สำนักทะเบียน|มหาวิทยาลัย|ประทับตรา|ใบ รบ|ปพ\.1|วุฒิการศึกษา)/i,
    matchEn: /(degree certificate|transcript|certificate of graduation|registrar|university seal|diploma|academic record)/i,
  },
  {
    slug: "mfa-legalization-education",
    titleTh: "นิติกรณ์เอกสารการศึกษาที่กรมการกงสุล",
    titleEn: "Thai MFA Legalisation of Education Documents",
    descTh:
      "การรับรองเอกสารการศึกษาไทยเพื่อใช้ต่างประเทศที่กองสัญชาติและนิติกรณ์ กรมการกงสุล ทั้งรับรองคำแปลและรับรองลายมือชื่อผู้มีอำนาจ ช่องทางยื่นด้วยตนเอง ทางไปรษณีย์ และระบบนัดหมายออนไลน์",
    descEn:
      "Legalising Thai education documents for overseas use at the Legalisation Division of the Department of Consular Affairs — translation certification versus signature certification, walk-in, postal and online-appointment channels.",
    matchTh: /(นิติกรณ์|กรมการกงสุล|กองสัญชาติ|รับรองลายมือชื่อ|แจ้งวัฒนะ|MFA|กงสุล)/i,
    matchEn: /(legalisation|legalization|consular affairs|MFA|signature certification|Chaeng Watthana)/i,
  },
  {
    slug: "certified-translation-education",
    titleTh: "แปลรับรองเอกสารการศึกษา ไทย–อังกฤษ และภาษาอื่น",
    titleEn: "Certified Translation of Education Documents",
    descTh:
      "หลักการแปลชื่อวุฒิ สาขาวิชา เกรด และระบบคะแนน GPA ให้ตรงกับต้นฉบับ การถอดชื่อ-นามสกุลตามหนังสือเดินทาง และเงื่อนไขที่กรมการกงสุลใช้ตรวจคำแปลก่อนรับรอง",
    descEn:
      "How degree titles, majors, grades and GPA scales are rendered faithfully, how names must match the passport, and the checks the Thai MFA applies to a translation before certifying it.",
    matchTh: /(แปล|คำแปล|GPA|เกรด|ถอดชื่อ|สะกดชื่อ|นักแปล|ภาษาอื่น)/i,
    matchEn: /(translation|translator|GPA|grade|transliteration|spelling of the name)/i,
  },
  {
    slug: "apostille-education-2027",
    titleTh: "Apostille กับเอกสารการศึกษา — ไทยเริ่มใช้ 28 กุมภาพันธ์ 2570",
    titleEn: "Apostille for Education Documents — Thailand from 28 February 2027",
    descTh:
      "อนุสัญญา Apostille (HCCH 1961) จะมีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 (28 February 2027) เอกสารการศึกษาที่จะใช้ในประเทศภาคีจะใช้ Apostille แผ่นเดียวแทนการรับรองซ้อนที่สถานทูต",
    descEn:
      "The HCCH 1961 Apostille Convention enters into force for Thailand on 28 February 2027; from that date education documents used in member states carry a single Apostille instead of a second embassy legalisation.",
    matchTh: /(Apostille|อโพสทิล|2570|2027|HCCH|อนุสัญญา)/i,
    matchEn: /(apostille|2027|HCCH|convention)/i,
  },
  {
    slug: "study-abroad-application",
    titleTh: "ยื่นสมัครเรียนต่อและขอวีซ่านักเรียนต่างประเทศ",
    titleEn: "University Applications and Student Visas Abroad",
    descTh:
      "ชุดเอกสารที่มหาวิทยาลัยและสถานทูตมักขอ ได้แก่ ทรานสคริปต์รับรอง ปริญญาบัตร หนังสือรับรองการเงิน ทะเบียนบ้าน สูติบัตร และหนังสือยินยอมของผู้ปกครองสำหรับผู้เยาว์",
    descEn:
      "The document set universities and consulates typically request: certified transcripts, degree certificate, proof of funds, house registration, birth certificate and parental consent for minors.",
    matchTh: /(สมัครเรียน|วีซ่านักเรียน|ทุน|สถานทูต|ผู้ปกครอง|ยินยอม|การเงิน|สูติบัตร|ทะเบียนบ้าน)/i,
    matchEn: /(admission|student visa|scholarship|proof of funds|parental consent|birth certificate|house registration)/i,
  },
  {
    slug: "credential-evaluation",
    titleTh: "ประเมินเทียบวุฒิต่างประเทศ (WES, ECE, ENIC-NARIC)",
    titleEn: "Foreign Credential Evaluation (WES, ECE, ENIC-NARIC)",
    descTh:
      "หน่วยประเมินวุฒิในสหรัฐ แคนาดา และยุโรปที่ต้องการทรานสคริปต์ส่งตรงจากสถาบัน (sealed / institution-sent) ความต่างระหว่างรายงานแบบ document-by-document และ course-by-course",
    descEn:
      "Evaluation bodies in the US, Canada and Europe that require sealed transcripts sent directly by the institution, and the difference between document-by-document and course-by-course reports.",
    matchTh: /(WES|ECE|NARIC|ENIC|ประเมินวุฒิ|เทียบวุฒิต่างประเทศ|course-by-course|ซองปิดผนึก)/i,
    matchEn: /(WES|ECE|NARIC|ENIC|credential evaluation|course-by-course|sealed envelope)/i,
  },
  {
    slug: "teacher-license-thailand",
    titleTh: "ใบอนุญาตประกอบวิชาชีพครูและครูต่างชาติในไทย",
    titleEn: "Thai Teaching Licence and Foreign Teachers",
    descTh:
      "คุรุสภา (ksp.or.th) กับหนังสืออนุญาตให้ประกอบวิชาชีพโดยไม่มีใบอนุญาตประกอบวิชาชีพ (Waiver) ใบอนุญาตประกอบวิชาชีพครู เอกสารวุฒิที่ต้องรับรอง และการเชื่อมกับใบอนุญาตทำงาน",
    descEn:
      "The Teachers' Council of Thailand (ksp.or.th): the waiver permitting teaching without a licence, the full teaching licence, which qualification documents must be legalised, and how this links to the work permit.",
    matchTh: /(ครู|คุรุสภา|waiver|ใบอนุญาตประกอบวิชาชีพ|โรงเรียน|สอน|TEFL)/i,
    matchEn: /(teacher|teaching licence|teaching license|Teachers' Council|waiver|TEFL|school)/i,
  },
  {
    slug: "school-enrollment-equivalency",
    titleTh: "เทียบวุฒิและเข้าเรียนในสถานศึกษาไทย",
    titleEn: "Equivalency and Enrolment in Thai Schools",
    descTh:
      "การเทียบวุฒิการศึกษาต่างประเทศเพื่อเข้าเรียนหรือสมัครสอบในไทย เอกสารที่โรงเรียนและมหาวิทยาลัยขอ และแนวปฏิบัติของกระทรวงศึกษาธิการและ สป.อว.",
    descEn:
      "Recognising foreign school and university qualifications for enrolment or entrance examinations in Thailand, the documents schools and universities request, and Ministry of Education / MHESI practice.",
    matchTh: /(เทียบวุฒิ|กระทรวงศึกษาธิการ|สพฐ|เข้าเรียน|โฮมสคูล|homeschool|GED|IGCSE|IB)/i,
    matchEn: /(equivalency|Ministry of Education|enrolment|enrollment|homeschool|GED|IGCSE|IB)/i,
  },
  {
    slug: "professional-licensing",
    titleTh: "ใช้วุฒิเพื่อขอใบอนุญาตวิชาชีพ (แพทย์ พยาบาล วิศวกร สถาปนิก)",
    titleEn: "Using Qualifications for Professional Licensing",
    descTh:
      "สภาวิชาชีพในไทยและต่างประเทศที่ตรวจวุฒิและ Certificate of Good Standing เช่น แพทยสภา สภาการพยาบาล สภาวิศวกร สภาสถาปนิก รวมถึงเอกสารที่ต้องนิติกรณ์",
    descEn:
      "Professional councils in Thailand and abroad that verify qualifications and certificates of good standing — Medical Council, Nursing Council, Council of Engineers, Architect Council — and which documents need legalisation.",
    matchTh: /(แพทยสภา|พยาบาล|วิศวกร|สถาปนิก|สภาวิชาชีพ|good standing|ใบอนุญาตวิชาชีพ|เภสัช)/i,
    matchEn: /(medical council|nursing|engineer|architect|professional council|good standing|licensure|pharmac)/i,
  },
  {
    slug: "embassy-attestation-education",
    titleTh: "รับรองเอกสารการศึกษาที่สถานทูตปลายทาง (ตะวันออกกลาง จีน เวียดนาม)",
    titleEn: "Destination-Embassy Attestation (Middle East, China, Vietnam)",
    descTh:
      "ประเทศที่ยังต้องรับรองซ้อนที่สถานทูตหรือสถานกงสุลในไทยหลังนิติกรณ์กรมการกงสุล เช่น สหรัฐอาหรับเอมิเรตส์ ซาอุดีอาระเบีย กาตาร์ จีน และเวียดนาม",
    descEn:
      "Countries that still require a second attestation at their embassy or consulate in Thailand after Thai MFA legalisation, including the UAE, Saudi Arabia, Qatar, China and Vietnam.",
    matchTh: /(สถานทูต|สถานกงสุล|เอมิเรตส์|ซาอุ|กาตาร์|จีน|เวียดนาม|ตะวันออกกลาง|attestation)/i,
    matchEn: /(embassy|consulate|UAE|Saudi|Qatar|China|Vietnam|Middle East|attestation)/i,
  },
];

export function getEducationCategoryDef(slug: string): EducationCategoryDef | undefined {
  return EDUCATION_FAQ_CATEGORIES.find((c) => c.slug === slug);
}